import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { FunctionalRootState } from '@share/utils';
import { fetchCertificateTemplates } from '@share/store/slices';
import type { ICertificateTemplate } from '@share/common-types';
import { ItemTypeEnum } from '@share/common-types';
import { useCertificateClubSiteId } from '@share/components';

const CATALOG_PAGE_SIZE = 10;

export type CertificateTemplatesSortOption = 'priceAsc' | 'priceDesc';

const ALL_ITEM_TYPES: ItemTypeEnum[] = [
  ItemTypeEnum.Hotel,
  ItemTypeEnum.Cruise,
  ItemTypeEnum.Condo,
];

export interface UseCertificateTemplatesCatalogParams {
  pageSize?: number;
  itemTypes: ItemTypeEnum[];
  sortOption: CertificateTemplatesSortOption;
}

export interface UseCertificateTemplatesCatalogResult {
  items: ICertificateTemplate[];
  totalCount: number;
  hasNextPage: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string;
  refetch: () => void;
  loadMore: () => void;
}

function resolveRequestItemTypes(selected: ItemTypeEnum[]): ItemTypeEnum[] {
  if (selected.length === 0) {
    return ALL_ITEM_TYPES;
  }
  return selected;
}

function sortOptionToOrder(sortOption: CertificateTemplatesSortOption): {
  orderBy: string;
  sortOrder: string;
} {
  if (sortOption === 'priceAsc') {
    return { orderBy: 'totalPrice', sortOrder: 'asc' };
  }
  return { orderBy: 'totalPrice', sortOrder: 'desc' };
}

export function useCertificateTemplatesCatalog(
  params: UseCertificateTemplatesCatalogParams,
): UseCertificateTemplatesCatalogResult {
  const { pageSize = CATALOG_PAGE_SIZE, itemTypes, sortOption } = params;
  const organizationId = useCertificateClubSiteId();

  const [pageNumber, setPageNumber] = useState(1);
  const requestIdRef = useRef(0);

  const dispatch = useDispatch();
  const { data, loading, loadingMore, error } = useSelector(
    (state: FunctionalRootState) => state.certificateTemplatesSearchStore,
  );

  const requestItemTypes = resolveRequestItemTypes(itemTypes);
  const { orderBy, sortOrder } = sortOptionToOrder(sortOption);

  const fetchParams = useMemo(
    () => ({
      pageNumber: 1,
      pageSize,
      orderBy,
      sortOrder,
      itemTypes: requestItemTypes,
      clubSiteIds: organizationId ? [organizationId] : [],
    }),
    [pageSize, orderBy, sortOrder, requestItemTypes, organizationId],
  );

  const refetch = useCallback(() => {
    setPageNumber(1);
    dispatch(fetchCertificateTemplates({ ...fetchParams, pageNumber: 1 }));
  }, [dispatch, fetchParams]);

  const loadMore = useCallback(() => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    dispatch(
      fetchCertificateTemplates(
        {
          ...fetchParams,
          pageNumber: nextPage,
        },
        { append: true },
      ),
    );
  }, [pageNumber, dispatch, fetchParams]);

  const itemTypesKey = requestItemTypes.slice().sort().join(',');

  useEffect(() => {
    const id = ++requestIdRef.current;
    setPageNumber(1);
    dispatch(fetchCertificateTemplates(fetchParams));

    return () => {
      if (id !== requestIdRef.current) {
        return;
      }
    };
  }, [dispatch, fetchParams, itemTypesKey]);

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasNextPage = (data?.hasNextPage ?? false) && !loadingMore;

  return {
    items,
    totalCount,
    hasNextPage,
    loading,
    loadingMore,
    error,
    refetch,
    loadMore,
  };
}
